/**
 * `counter` and `status`: bookkeeping on your own board objects by hand.
 *
 * - `damage` is marked damage (CR 142); `buff` is buff counters (CR 701-705); any other kind is a
 *   generic counter (CR 742). Values never go below zero. XP is a player resource (CR 729-733), not a
 *   counter: use the `xp` command.
 * - Statuses: `stunned` (CR 424), `empowered` (CR 442), or any other name.
 */
import { type Rejection, reject } from '../../kernel/errors';
import { refOf } from '../events/refs';
import { hasStatus } from '../state/objects';
import { type CommandContext, LIMITS, actorObject, emit } from './context';
import { canTurn } from './exhaust';
import type { TableCommand } from './types';

type Counter = Extract<TableCommand, { t: 'counter' }>;
type StatusCmd = Extract<TableCommand, { t: 'status' }>;

/** Allowed custom counter and status names. */
export const MARKER_NAME_PATTERN = /^[a-z][a-zA-Z0-9-]{0,31}$/;

/** Changes damage, buffs or a custom counter on one of the actor's board objects. */
export function handleCounter(ctx: CommandContext, cmd: Counter): Rejection | undefined {
  if (cmd.kind === 'xp') return reject('invalidCommand', 'XP is a player resource (CR 729); use the xp command');
  if (!MARKER_NAME_PATTERN.test(cmd.kind)) return reject('invalidCommand', 'Invalid counter kind');
  if (!Number.isInteger(cmd.delta) || cmd.delta === 0 || Math.abs(cmd.delta) > LIMITS.counterDelta) {
    return reject('outOfRange', `delta must be a non-zero integer up to ±${LIMITS.counterDelta}`);
  }
  const object = actorObject(ctx, cmd.object);
  if ('rejected' in object) return object;
  if (!canTurn(object)) return reject('invalidCommand', 'Counters go on permanents, runes and legends on the board');
  const current =
    cmd.kind === 'damage' ? object.damage : cmd.kind === 'buff' ? object.buffs : (object.counters[cmd.kind] ?? 0);
  const value = Math.max(0, current + cmd.delta);
  if (value === current) return reject('noChange');
  if (cmd.kind === 'damage') object.damage = value;
  else if (cmd.kind === 'buff') object.buffs = value;
  else if (value === 0) Reflect.deleteProperty(object.counters, cmd.kind);
  else object.counters[cmd.kind] = value;
  emit(ctx, {
    type: 'counterChanged',
    actor: ctx.actor,
    object: refOf(ctx.draft, object),
    kind: cmd.kind,
    delta: value - current,
    value,
  });
  return undefined;
}

/** Sets or clears a status on one of the actor's board objects. */
export function handleStatus(ctx: CommandContext, cmd: StatusCmd): Rejection | undefined {
  if (!MARKER_NAME_PATTERN.test(cmd.status)) return reject('invalidCommand', 'Invalid status name');
  const object = actorObject(ctx, cmd.object);
  if ('rejected' in object) return object;
  if (!canTurn(object)) return reject('invalidCommand', 'Statuses go on permanents, runes and legends on the board');
  if (hasStatus(object, cmd.status) === cmd.on) return reject('noChange');
  if (cmd.on) object.statuses.push({ kind: cmd.status, source: null, until: null });
  else object.statuses = object.statuses.filter((s) => s.kind !== cmd.status);
  emit(ctx, {
    type: 'statusChanged',
    actor: ctx.actor,
    object: refOf(ctx.draft, object),
    status: cmd.status,
    on: cmd.on,
  });
  return undefined;
}
